/**
 * Maps blog posts (by category and per-slug override) to the most-relevant
 * /insurance-solutions hub pages. Drives the "Related Insurance Solutions"
 * rail rendered at the bottom of every blog post.
 *
 * Why this exists:
 *   Modern Google ranking is heavily weighted toward TOPICAL AUTHORITY —
 *   how tightly your content clusters around a subject. A blog post that
 *   internally links to 3 closely-related hub pages tells Googlebot that
 *   the topic is a real focus area, not a one-off. It also lifts the
 *   hub-page traffic by surfacing them on every related article.
 *
 * How to read this file:
 *   • CATEGORY_DEFAULTS — fallback picks per category slug.
 *   • PER_SLUG_OVERRIDES — explicit per-post mapping, overrides the
 *     category default when a post fits a more specific cluster (e.g.
 *     the hurricane post is in Personal but should link to /extreme-
 *     weather, not the generic /personal-insurance only).
 *   • EVERY array MUST contain exactly 3 valid hub-page slugs that
 *     exist in /app/frontend/src/lib/services.js. The runtime guards
 *     against drift (unknown slugs are dropped), but eyeball it before
 *     shipping.
 *
 * Anchor-text strategy:
 *   The label is intentionally NOT just the hub page H1. We use
 *   descriptive intent-phrases ("Personal & home insurance",
 *   "Cyber liability for small business") because Google reads anchor
 *   text as a topic signal — duplicate exact-match anchors look spammy,
 *   but synonym-rich anchors compound topical authority.
 */

import { SERVICES } from "./services";

// Default trio per blog category. Order matters — the first item is
// the primary cluster anchor for that category.
const CATEGORY_DEFAULTS = {
    business: [
        "business-insurance",
        "commercial-lines",
        "cyber-insurance",
    ],
    personal: [
        "personal-insurance",
        "extreme-weather",
        "housing-community-services",
    ],
    health: [
        "health-insurance",
        "supplemental-benefits",
        "life-insurance",
    ],
    "special-risk": [
        "cyber-insurance",
        "business-insurance",
        "commercial-lines",
    ],
};

// Per-slug overrides. Keep this list short and intentional. If a slug
// is NOT listed, the category default is used.
const PER_SLUG_OVERRIDES = {
    // Business cluster
    "do-i-need-workers-comp-insurance": [
        "business-insurance",
        "commercial-lines",
        "housing-community-services",
    ],
    "general-liability-vs-bop": [
        "business-insurance",
        "commercial-lines",
        "cyber-insurance",
    ],
    "commercial-auto-vs-personal-auto-insurance": [
        "commercial-lines",
        "business-insurance",
        "personal-insurance",
    ],

    // Personal cluster — both hurricane / coastal posts route to the
    // /extreme-weather hub first because that's the higher-intent
    // search target.
    "coastal-home-insurance-wind-and-flood": [
        "extreme-weather",
        "personal-insurance",
        "housing-community-services",
    ],
    "hurricane-insurance-wind-vs-flood-deductibles": [
        "extreme-weather",
        "personal-insurance",
        "housing-community-services",
    ],

    // Health cluster — Medicare-adjacent posts route to /health-insurance
    // primary, /supplemental-benefits secondary, /life-insurance tertiary.
    "medicare-advantage-2026-changes": [
        "health-insurance",
        "supplemental-benefits",
        "life-insurance",
    ],
    "medigap-medicare-supplement-2026": [
        "health-insurance",
        "supplemental-benefits",
        "life-insurance",
    ],
    "medicare-annual-election-period-2026": [
        "health-insurance",
        "supplemental-benefits",
        "life-insurance",
    ],
    "medicaid-changes-2026": [
        "health-insurance",
        "supplemental-benefits",
        "housing-community-services",
    ],
    "medicare-part-c-2026-guide": [
        "health-insurance",
        "supplemental-benefits",
        "life-insurance",
    ],

    // Special-risk cluster — both cyber posts surface cyber FIRST.
    "why-small-businesses-need-cyber-insurance": [
        "cyber-insurance",
        "business-insurance",
        "commercial-lines",
    ],
    "cyber-insurance-small-business-2026": [
        "cyber-insurance",
        "business-insurance",
        "commercial-lines",
    ],
};

// Short, descriptive anchor labels keyed by SERVICE slug. We DON'T use
// the H1 from services.js because we want anchor variation across the
// site (Google rewards synonym-rich internal links).
const ANCHOR_OVERRIDES = {
    "business-insurance": {
        label: "Small business insurance overview",
        blurb: "BOP, GL, workers' comp, and the policies a small business actually needs.",
    },
    "commercial-lines": {
        label: "Commercial lines & business auto",
        blurb: "Mid-market property, fleet, and commercial vehicle coverage explained.",
    },
    "cyber-insurance": {
        label: "Cyber liability for small business",
        blurb: "Ransomware, breach response, and regulatory defense — what carriers actually require in 2026.",
    },
    "personal-insurance": {
        label: "Home, auto & personal-lines",
        blurb: "Standard personal lines for homeowners, renters, and drivers across all 7 states.",
    },
    "extreme-weather": {
        label: "Coastal, wind & flood coverage",
        blurb: "Hurricane, named-storm, and NFIP / private flood placements for coastal homes.",
    },
    "housing-community-services": {
        label: "Housing & community-services insurance",
        blurb: "Coverage for landlords, multi-unit housing, and community-service organizations.",
    },
    "health-insurance": {
        label: "Health, Medicare & Medicaid",
        blurb: "Medicare Advantage, Medigap, Marketplace, and Medicaid placements with a licensed agent.",
    },
    "supplemental-benefits": {
        label: "Supplemental & voluntary benefits",
        blurb: "Accident, critical-illness, hospital indemnity, and voluntary worksite coverage.",
    },
    "life-insurance": {
        label: "Term & whole life insurance",
        blurb: "Family safety nets, business succession, key-person, and final-expense conversations.",
    },
};

/**
 * Return up to 3 hub-page picks for a given blog post.
 *
 * @param {{slug: string, category: string}} post
 * @returns {Array<{slug, path, label, blurb}>}
 */
export function getRelatedSolutionsForPost(post) {
    if (!post) return [];
    const overrides = PER_SLUG_OVERRIDES[post.slug];
    const slugs =
        overrides ||
        CATEGORY_DEFAULTS[post.category] ||
        CATEGORY_DEFAULTS.business;

    // Resolve each slug against SERVICES (drop any drifted entry).
    return slugs
        .map((s) => {
            const svc = SERVICES.find((x) => x.slug === s);
            if (!svc) return null;
            const anchor = ANCHOR_OVERRIDES[s] || {
                label: svc.title,
                blurb: svc.intro?.slice(0, 110) || "",
            };
            return {
                slug: s,
                path: svc.path,
                label: anchor.label,
                blurb: anchor.blurb,
            };
        })
        .filter(Boolean)
        .slice(0, 3);
}
